import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Text, Flex, Button, useStyleConfig} from '@chakra-ui/react'
import {TextTitle} from '../../text-title'
import {TextTitleDescription} from '../../text-title-description'
import {remCalc} from '../../../../theme/_utils'
import {Link} from 'react-router-dom'
import {getAlignmentStyles, calculateVerticalPadding} from '../../utils'
import {CarouselPrevIcon, CarouselNextIcon} from '../../../icons'
import {useGetCategoriesData} from '../../../../custom-api/hooks/use-get-categories-data'
import {safeBuildUrl, getGlobalLink} from '../../../../utils/contentstack-utils'
import HeroBannerCountDownV2 from '../../hero-banner-countdown-v2'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import SanitizedHtml from '../../../html-sanitizer'
import InformativeLabel from '../../../informative-label/partials/InformativeLabel'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {useSeoH1} from '../../../../pages/home'

const REVEAL_STEP_DELAY = 200
const REVEAL_DURATION = 600
const REVEAL_KEYFRAMES = [
    {opacity: 0, transform: `translateY(${remCalc(40)})`},
    {opacity: 1, transform: 'none'},
]

export const HeroBannerV2Text = ({
    maxWidth = remCalc(1920),
    textBlock,
    variant,
    hasPaddings,
    countdown,
    buttonText,
    buttonText2,
    isSmallHeight = false,
    isMediumHeight = false,
    isFirstElement,
    slidesCount = 0,
    activeSlideIndex = 0,
    slideProgress = 0,
    onPrev,
    onNext,
    isBentoLayout = false,
    isCarouselV2 = false,
}) => {
    const styles = useStyleConfig('HeroBannerText', {variant})
    const {buildUrl, locale} = useMultiSite()

    const stylesBasedOnAlignment = getAlignmentStyles(textBlock?.desktopAlignment, styles)
    const {marginBottom: alignmentMarginBottom, ...textContentAlignmentStyles} =
        stylesBasedOnAlignment.textContent
    const desktopTextAlignment = textBlock?.desktopAlignment?.includes('center') ? 'center' : 'left'
    const responsiveTextAlign = {base: 'left', lg: desktopTextAlignment}
    const containerJustify = stylesBasedOnAlignment.container.justifyContent
    const containerJustifyLg =
        containerJustify && typeof containerJustify === 'object'
            ? containerJustify.lg
            : containerJustify
    const textContentAlignLg = stylesBasedOnAlignment.textContent.alignItems ?? 'stretch'
    const verticalPadding = calculateVerticalPadding('', hasPaddings)

    const isContainerCol = variant === 'hero-banner-v2-container-column' || false

    const categoriesData = useGetCategoriesData()
    const {data: productsData} = useProducts(
        {
            parameters: {
                ids:
                    buttonText?.productsfcc?.data?.length > 0
                        ? buttonText.productsfcc.data[0].id
                        : '',
                expand: ['availability', 'images'],
            },
            headers: {
                'x-debug': 'hero-banner-v2-index',
            },
        },
        {
            enabled: buttonText?.productsfcc?.data?.length > 0,
        },
    )
    const {url, label, isAbsolute} = getGlobalLink({
        link: buttonText,
        categoriesData,
        productsData,
        locale: locale?.alias || locale?.id?.toLowerCase(),
    })

    const {data: productsData2} = useProducts(
        {
            parameters: {
                ids:
                    buttonText2?.productsfcc?.data?.length > 0
                        ? buttonText2.productsfcc.data[0].id
                        : '',
                expand: ['availability', 'images'],
            },
            headers: {
                'x-debug': 'hero-banner-v2-index-btn2',
            },
        },
        {
            enabled: buttonText2?.productsfcc?.data?.length > 0,
        },
    )
    const {
        url: url2,
        label: label2,
        isAbsolute: isAbsolute2,
    } = getGlobalLink({
        link: buttonText2,
        categoriesData,
        productsData: productsData2,
        locale: locale?.alias || locale?.id?.toLowerCase(),
    })

    const buttonProps = isAbsolute
        ? {
              as: 'a',
              href: url,
              target: '_blank',
              rel: 'noopener noreferrer',
          }
        : {
              as: Link,
              to: safeBuildUrl(url, buildUrl),
          }

    const buttonProps2 = isAbsolute2
        ? {
              as: 'a',
              href: url2,
              target: '_blank',
              rel: 'noopener noreferrer',
          }
        : {
              as: Link,
              to: safeBuildUrl(url2, buildUrl),
          }

    const countdownEndDate = countdown?.[0]?.end_date
    const hasCountdown = Boolean(countdownEndDate)
    const hasTwoCtas = !!(label && url && label2 && url2)
    const ctaFlex = hasTwoCtas ? {base: '0 1 auto', md: '0 0 auto'} : undefined
    const ctaWidth = hasTwoCtas ? {base: '100%', md: 'auto'} : undefined

    // const inlinePaddingEnabled =
    //     textBlock?.desktopAlignment !== 'middle-right' &&
    //     textBlock?.desktopAlignment !== 'middle-left'
    // const normalPadding = {base: remCalc(16), lg: remCalc(40)}
    // const customPadding = {base: 'unset', sm: 'unset', md: 'unset', lg: remCalc(16), '2xl': 'unset'}

    const {hasSeoH1} = useSeoH1()

    // The first element is above the fold, so it is not animated to avoid delaying LCP
    const [isInView, setIsInView] = useState(!!isFirstElement)
    const rootRef = useRef(null)
    const previousSlideIndexRef = useRef(activeSlideIndex)

    useEffect(() => {
        const el = rootRef.current
        if (isInView || !el) return
        if (typeof IntersectionObserver === 'undefined') {
            setIsInView(true)
            return
        }
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) setIsInView(true)
            },
            {threshold: 0.2},
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [isInView])

    useEffect(() => {
        if (previousSlideIndexRef.current === activeSlideIndex) return

        previousSlideIndexRef.current = activeSlideIndex

        if (!isInView || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return

        const animations = Array.from(rootRef.current.querySelectorAll('[data-reveal-step]'))
            .filter((el) => typeof el.animate === 'function')
            .map((el) =>
                el.animate(REVEAL_KEYFRAMES, {
                    duration: REVEAL_DURATION,
                    delay: Number(el.dataset.revealStep) * REVEAL_STEP_DELAY,
                    easing: 'ease-out',
                    fill: 'backwards',
                }),
            )

        return () => animations.forEach((animation) => animation.cancel())
    }, [activeSlideIndex, isInView])

    const getRevealProps = (step) => {
        const delay = step * REVEAL_STEP_DELAY

        return {
            'data-reveal-step': step,
            opacity: isInView ? 1 : 0,
            transform: isInView ? 'none' : REVEAL_KEYFRAMES[0].transform,
            transition: `opacity ${REVEAL_DURATION}ms ease-out ${delay}ms, transform ${REVEAL_DURATION}ms ease-out ${delay}ms`,
            sx: {'@media (prefers-reduced-motion: reduce)': {transition: 'none'}},
        }
    }

    return (
        <Flex
            maxW={maxWidth}
            data-testid={variant}
            px={
                isBentoLayout || isContainerCol
                    ? ['xsmall.base', 'xsmall.sm', 'xsmall.md', 'xsmall.lg']
                    : ['page.base', 'page.base', 'page.lg', 'page.lg']
            }
            pt={isCarouselV2 || isContainerCol ? undefined : [0, 0, 'xsmall-1.md', 0]}
            pb={
                isCarouselV2 || isBentoLayout || isContainerCol
                    ? undefined
                    : hasCountdown
                    ? ['xsmall.base', 'xsmall.sm', 'xsmall.md', 'xsmall.lg']
                    : ['xsmall.base', 'xsmall.sm', 'xsmall-1.md', 0]
            }
            flex={1}
            zIndex={2}
            flexDir="column"
            w="100%"
            ref={rootRef}
        >
            <Flex
                {...stylesBasedOnAlignment.container}
                layerStyle={undefined}
                px={0}
                flexDir={{base: 'column', md: 'row'}}
                alignItems={{base: 'stretch', md: 'flex-end'}}
                justifyContent={{base: 'flex-end', md: containerJustifyLg}}
                gap={['small.base', 'small.sm', 'small.md', 'small.lg']}
                pb={remCalc(0)}
                flex={1}
            >
                <Flex
                    textAlign={responsiveTextAlign}
                    {...textContentAlignmentStyles}
                    alignItems={{base: 'flex-start', lg: textContentAlignLg}}
                    maxW={{md: remCalc(460), lg: 'none'}}
                    gap={
                        isBentoLayout
                            ? ['xxsmall.base', 'xxsmall.sm', 'xxsmall.md', 'xxsmall.lg']
                            : 0
                    }
                    marginBottom={
                        isCarouselV2 || isBentoLayout || hasCountdown ? 0 : alignmentMarginBottom
                    }
                    px={0}
                    pt={isBentoLayout ? verticalPadding : undefined}
                    data-testid="hero-banner-v2-text-flex"
                >
                    <Flex
                        {...styles.v2BadgeLabel}
                        pb={0}
                        mb="xxsmall-1.base"
                        {...getRevealProps(0)}
                    >
                        {textBlock?.label && (
                            <InformativeLabel
                                variant="priceInfo"
                                label={<SanitizedHtml as="span" html={textBlock.label} />}
                            />
                        )}
                        {textBlock?.subtitle && (
                            <TextTitle
                                alignment={responsiveTextAlign}
                                data-testid="hero-banner-v2-text-subtitle"
                                mb={0}
                                fontFamily="body"
                                {...styles.v2TextTitle}
                                color="foreground.neutral.inverse"
                            >
                                <SanitizedHtml as={Box} html={textBlock?.subtitle} />
                            </TextTitle>
                        )}
                    </Flex>

                    <Flex
                        flexDirection="column"
                        gap={{
                            base: 'xsmall-1.base',
                            md: 'xsmall-1.md',
                            lg: 'xsmall-1.lg',
                            xl: 'xsmall-1.xl',
                        }}
                    >
                        <TextTitleDescription
                            alignment={responsiveTextAlign}
                            title={
                                <Text {...styles.v2TextTitleMain} fontFamily="heading">
                                    <SanitizedHtml
                                        role="heading"
                                        as={!hasSeoH1 && isFirstElement ? 'h1' : 'h2'}
                                        html={textBlock?.title}
                                    />
                                </Text>
                            }
                            titleSpacing={
                                isSmallHeight
                                    ? remCalc(16)
                                    : isMediumHeight
                                    ? remCalc(24)
                                    : {
                                          base: 'xxsmall.base',
                                          md: 'xxsmall.md',
                                          lg: 'xxsmall.lg',
                                          xl: 'xxsmall.xl',
                                      }
                            }
                            color="foreground.neutral.inverse"
                            childrenTextColor="foreground.neutral.inverse"
                            mb={0}
                            noMinHeight
                            childrenTextStyle="body-large"
                            {...getRevealProps(1)}
                        >
                            <SanitizedHtml as={Box} html={textBlock?.bodyCopy} />
                        </TextTitleDescription>

                        <Flex
                            {...styles.ctas}
                            flexDirection="row"
                            flexWrap="nowrap"
                            alignItems="flex-start"
                            justifyContent="flex-start"
                            w={hasTwoCtas ? {base: '100%', md: 'auto'} : 'auto'}
                            pb={
                                isCarouselV2
                                    ? [
                                          'xsmall.base',
                                          'xxsmall-1.sm',
                                          'xxsmall-1.md',
                                          'xxsmall-1.lg',
                                      ]
                                    : ['xsmall.base', 'xsmall.sm', 'xsmall.md', 'xsmall.lg']
                            }
                            {...getRevealProps(2)}
                        >
                            {label && url && (
                                <Box textAlign={responsiveTextAlign} flex={ctaFlex} minW={0}>
                                    <Button
                                        {...buttonProps}
                                        variant="outline-not-rounded"
                                        size="xl"
                                        {...styles.HeroBannerButton}
                                        {...styles.v2HeroBannerButton}
                                        w={ctaWidth}
                                        maxWidth={{base: remCalc(140), md: '100%'}}
                                        paddingX={{
                                            base: remCalc(16),
                                            md: remCalc(24),
                                        }}
                                    >
                                        {label}
                                    </Button>
                                </Box>
                            )}
                            {label2 && url2 && (
                                <Box textAlign={responsiveTextAlign} flex={ctaFlex} minW={0}>
                                    <Button
                                        {...buttonProps2}
                                        variant={'ghost-not-rounded'}
                                        size="xl"
                                        {...styles.HeroBannerButton}
                                        {...styles.v2HeroBannerButton}
                                        w={ctaWidth}
                                        color="foreground.neutral.inverse"
                                        _hover={{bg: 'oklch(0.226 0.065 274 / 0.04)'}}
                                        maxWidth={{base: remCalc(140), md: '100%'}}
                                        paddingX={{
                                            base: remCalc(16),
                                            md: remCalc(24),
                                        }}
                                    >
                                        {label2}
                                    </Button>
                                </Box>
                            )}
                        </Flex>
                    </Flex>
                </Flex>

                {countdownEndDate && (
                    <Flex
                        alignItems="flex-end"
                        justifyContent={{base: 'center', md: 'flex-end'}}
                        w={{base: '100%', md: 'auto'}}
                        ml={{base: 0, md: 'auto'}}
                        pb={isCarouselV2 ? 0 : [0, 0, 'xxsmall-1.md', 'xxsmall-1.lg']}
                        {...getRevealProps(3)}
                    >
                        <HeroBannerCountDownV2
                            endingDate={countdownEndDate}
                            variant={variant}
                            w={{base: '100%', md: 'auto'}}
                            justifyContent={{base: 'space-around', md: 'center'}}
                        />
                    </Flex>
                )}
            </Flex>

            {slidesCount > 1 && (
                <Flex
                    {...styles.v2CarouselBar}
                    display={{base: 'none', md: 'flex'}}
                    px={0}
                    mt={['small-1.base', 'small-1.sm', 'small-1.md', 'small-1.lg']}
                    pt={isCarouselV2 ? 0 : undefined}
                    pb={
                        isCarouselV2
                            ? ['xsmall.base', 'xsmall.sm', 'xsmall.md', 'xsmall.lg']
                            : ['xsmall.base', 'xsmall.sm', 'xsmall.md', 'xsmall.lg']
                    }
                    aria-hidden="true"
                >
                    {Array.from({length: slidesCount}).map((_, idx) => {
                        const barWidth =
                            idx < activeSlideIndex
                                ? '100%'
                                : idx === activeSlideIndex
                                ? `${slideProgress}%`
                                : '0%'
                        return (
                            <Box key={idx} {...styles.v2CarouselTrack}>
                                <Box {...styles.v2CarouselFill} style={{width: barWidth}} />
                            </Box>
                        )
                    })}
                    <Flex>
                        <Button
                            {...styles.v2CarouselArrow}
                            aria-label="Previous slide"
                            onClick={(e) => {
                                e.stopPropagation()
                                onPrev?.()
                            }}
                        >
                            <CarouselPrevIcon />
                        </Button>
                        <Button
                            {...styles.v2CarouselArrow}
                            aria-label="Next slide"
                            onClick={(e) => {
                                e.stopPropagation()
                                onNext?.()
                            }}
                        >
                            <CarouselNextIcon />
                        </Button>
                    </Flex>
                </Flex>
            )}
        </Flex>
    )
}

HeroBannerV2Text.propTypes = {
    textBlock: PropTypes.shape({
        label: PropTypes.string,
        subtitle: PropTypes.string,
        title: PropTypes.string,
        bodyCopy: PropTypes.string,
        desktopAlignment: PropTypes.string,
        tabletMobileAlignment: PropTypes.string,
    }),
    buttonText: PropTypes.object,
    buttonText2: PropTypes.object,
    maxWidth: PropTypes.string,
    variant: PropTypes.string,
    hasPaddings: PropTypes.bool,
    countdown: PropTypes.array,
    isSmallHeight: PropTypes.bool,
    isMediumHeight: PropTypes.bool,
    isFirstElement: PropTypes.bool,
    slidesCount: PropTypes.number,
    activeSlideIndex: PropTypes.number,
    slideProgress: PropTypes.number,
    onPrev: PropTypes.func,
    onNext: PropTypes.func,
    isBentoLayout: PropTypes.bool,
    isCarouselV2: PropTypes.bool,
}
